import React from 'react';
import { computed, observable, action, makeObservable } from 'mobx';
import { observer } from 'src/utils/mobx-react';
import {
    TPWrapper,
    TopUpLimitsInput,
    ReadonlyCardHeaderRow,
    ReadonlyCardPeriod,
    ReadonlyCardAmount,
    ReadonlyCardAvailableAmountRow,
    ReadonlyPendingLimitFooter,
    CalendarLimitAvailableAndResetRow,
} from 'src/domains/players/webview/components/Account/limitsTab/rollingNetDepositLimit/DepositLimitsProcedure.style';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { Messages } from 'src/domains/layouts/webview/components/Messages/Messages';
import { UsersState } from 'src/domains/players/state/UsersState';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { Amount } from 'src_common/common/amount/Amount';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { DateTime } from 'src_common/utils/time/time';
import { SelectOptionType } from 'src/domains/players/webview/components/form/select/Select';
import { getRollingLimits } from 'src_server/trpc/types/responsibleGambling';
import { useCommon } from 'src/domains/common/Common';
import { validateAmountNotRequired } from 'src/domains/players/webview/components/ValidatorsNew';
import { BasicDataModel } from 'src/domains/players/state/BasicDataModel';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { assertNever } from 'src_common/common/assertNever';
import { DepositLimitsPanel } from './DepositLimitsPanel';
import {
    shouldUseCalendarPeriodLabels,
    resolveAccountLimitWindowType,
    DepositLimitWindowType,
} from 'src/domains/players/webview/components/Account/limitsTab/rollingNetDepositLimit/depositLimitType';

export class RollingNetDepositLimitItemState {
    @observable public refAmountInput: HTMLInputElement | null;

    public limitAmount: FormInputState<string, Amount | undefined>;

    public constructor(
        private readonly configComponents: ConfigComponents,
        public readonly usersState: UsersState,
        private readonly clearInfoMessage: () => void,
        public readonly getLimits: () => getRollingLimits.LimitsObjectSchemaType | undefined,
        private readonly amountUsed: () => number
    ) {
        makeObservable(this);
        this.refAmountInput = null;

        this.limitAmount = FormInputState.new('').map(validateAmountNotRequired);
    }

    public setAmountInputRef = (node: HTMLInputElement | null): void => {
        this.refAmountInput = node;
    };

    public onBlur = (): void => {
        const amount = parseFloat(this.limitAmount.value);
        if (isNaN(amount) === true || amount === 0) {
            return this.limitAmount.setValue('');
        }
        return this.limitAmount.setValue(amount.toFixed(2));
    };

    @action public onChangeInput = (event: React.SyntheticEvent<HTMLInputElement>): void => {
        const newValue = event.currentTarget.value;
        this.clearInfoMessage();
        this.limitAmount.setValue(newValue);
    };

    @action public resetValue = (): void => {
        this.limitAmount.reset();
    };

    @computed private get inputDepositLimitType(): number {
        return this.getLimits()?.activeLimitValue ?? 0;
    }

    @computed public get activeLimit(): string {
        const activeValue = this.getLimits()?.activeLimitValue;
        return activeValue === undefined
            ? 'Not set'
            : this.configComponents.precision
                  .newFromOld(activeValue)
                  .format(this.usersState.currency, { withoutCurrencySymbol: true });
    }

    @computed public get amountAvailable(): string {
        const alreadyUsed = this.amountUsed();
        const raw = Math.max(0, this.inputDepositLimitType - alreadyUsed);

        return this.configComponents.precision.newFromOld(raw).format(this.usersState.currency);
    }

    @computed public get depositLimitInputCeilingPence(): number | undefined {
        const limits = this.getLimits();
        if (limits === undefined) {
            return undefined;
        }
        const active = limits.activeLimitValue;
        const regulatoryMax = limits.limitValueMax;
        if (active === undefined) {
            return undefined;
        }
        if (regulatoryMax === null || regulatoryMax === undefined) {
            return active;
        }
        return Math.min(active, regulatoryMax);
    }

    @computed public get formattedDepositLimitCeiling(): string {
        const ceiling = this.depositLimitInputCeilingPence;
        return ceiling === undefined
            ? this.configComponents.precision.newFromAnything(0).format(this.usersState.currency)
            : this.configComponents.precision.newFromOld(ceiling).format(this.usersState.currency);
    }

    @computed public get errorMaxLimitExceeded(): boolean {
        const ceiling = this.depositLimitInputCeilingPence;
        const currentValue =
            this.limitAmount.result.value.type === 'ok' ? this.limitAmount.result.value.data?.multiply(100) : undefined;
        const enteredPence = parseInt(currentValue?.value ?? '0', 10);
        return ceiling !== undefined && enteredPence > ceiling;
    }

    @computed public get isError(): boolean {
        return this.limitAmount.result.value.type === 'error' || this.errorMaxLimitExceeded;
    }

    public getFormattedDate = (type: 'daily' | 'weekly' | 'monthly'): string => {
        const today = new Date();
        let targetDate = new Date();

        switch (type) {
            case 'daily': {
                //tomorrow
                targetDate.setDate(today.getDate() + 1);
                break;
            }
            case 'weekly': {
                // nextMonday
                const dayOfWeek = today.getDay();
                const daysUntilMonday = (8 - dayOfWeek) % 7;
                const correctedDaysUntilMonday = daysUntilMonday === 0 ? 7 : daysUntilMonday;

                targetDate.setDate(today.getDate() + correctedDaysUntilMonday);
                break;
            }
            case 'monthly': {
                // firstOfNextMonth
                targetDate = new Date(Date.UTC(today.getFullYear(), today.getMonth() + 1, 1));
                break;
            }
        }

        return Intl.DateTimeFormat('pl-PL', {
            timeZone: 'Europe/London',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        }).format(targetDate);
    };
}

const formatActivationCountdown = (millisecondsUntil: number): string => {
    const ms = Math.max(0, millisecondsUntil);
    const totalMinutes = Math.floor(ms / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return `${hours}h ${minutes}m`;
};

const pendingConfirmationMessage = (
    configComponents: ConfigComponents,
    usersState: UsersState,
    pendingLimitValue: number,
    pendingRequestedAt: string
): JSX.Element | undefined => {
    if (pendingLimitValue === 0) {
        return undefined;
    }

    const requestedAt = DateTime.from(pendingRequestedAt);
    const effectiveAt = requestedAt?.addDays(1);

    if (effectiveAt === undefined) {
        return undefined;
    }

    const millisecondsUntil = effectiveAt.diffInMilliseconds(DateTime.current());

    if (Number.isNaN(millisecondsUntil)) {
        return undefined;
    }

    const limitAmount = configComponents.precision.newFromOld(pendingLimitValue).format(usersState.currency);
    const timeRemaining = formatActivationCountdown(millisecondsUntil);

    return (
        <I18n
            langKey='account.rolling-net-deposit-limits.pending-confirmation-countdown'
            defaultText='Pending Confirmation {limitAmount} limit in {timeRemaining}'
            params={{ limitAmount, timeRemaining }}
        />
    );
};

export const renderRollingLimitPending = (
    configComponents: ConfigComponents,
    usersState: UsersState,
    limits: getRollingLimits.LimitsObjectSchemaType | undefined,
    periodForDataTest: string
): React.ReactElement | null => {
    const pendingLimitValue = limits?.pendingLimitValue;
    const pendingRequestedAt = limits?.pendingRequestedAt;

    if (
        pendingLimitValue === null ||
        pendingLimitValue === undefined ||
        pendingRequestedAt === null ||
        pendingRequestedAt === undefined
    ) {
        return null;
    }

    const message = pendingConfirmationMessage(configComponents, usersState, pendingLimitValue, pendingRequestedAt);

    if (message === undefined) {
        return null;
    }

    return (
        <ReadonlyPendingLimitFooter data-test={`limit-set-message-${periodForDataTest}`}>
            {message}
        </ReadonlyPendingLimitFooter>
    );
};
const renderLabel = (
    languagesState: LanguagesState,
    depositLimitTime: 'daily' | 'weekly' | 'monthly',
    useCalendarPeriodLabels: boolean
): string => {
    if (useCalendarPeriodLabels) {
        if (depositLimitTime === 'daily') {
            return languagesState.getTranslation('account.net-deposit-limit.label.calendar.daily', 'Daily Limit');
        } else if (depositLimitTime === 'weekly') {
            return languagesState.getTranslation('account.net-deposit-limit.label.calendar.weekly', 'Weekly Limit');
        } else if (depositLimitTime === 'monthly') {
            return languagesState.getTranslation('account.net-deposit-limit.label.calendar.monthly', 'Monthly Limit');
        }
    } else {
        if (depositLimitTime === 'daily') {
            return languagesState.getTranslation('account.net-deposit-limit.label.oneDay', '1 Day');
        } else if (depositLimitTime === 'weekly') {
            return languagesState.getTranslation('account.net-deposit-limit.label.sevenDays', '7 Days');
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        } else if (depositLimitTime === 'monthly') {
            return languagesState.getTranslation('account.net-deposit-limit.label.thirtyDays', '30 Days');
        }
    }

    return assertNever('renderLabelTopUpLimitItem', depositLimitTime);
};

const toWindowLengthInDays = (depositLimitTime: 'daily' | 'weekly' | 'monthly'): 1 | 7 | 30 => {
    if (depositLimitTime === 'daily') {
        return 1;
    }
    if (depositLimitTime === 'weekly') {
        return 7;
    }
    return 30;
};

/**
 * Single-limit edit form period select (keys 1 / 7 / 30 for BE).
 * Calendar: Daily / Weekly / Monthly. Rolling: 1 Day / 7 Days / 30 Days.
 */
export const getAccountDepositLimitPeriodSelectOptions = (
    languagesState: LanguagesState,
    limitTypeByWindow: Record<1 | 7 | 30, DepositLimitWindowType>
): SelectOptionType[] => {
    return [
        { '1': renderLabel(languagesState, 'daily', shouldUseCalendarPeriodLabels(limitTypeByWindow[1])) },
        { '7': renderLabel(languagesState, 'weekly', shouldUseCalendarPeriodLabels(limitTypeByWindow[7])) },
        { '30': renderLabel(languagesState, 'monthly', shouldUseCalendarPeriodLabels(limitTypeByWindow[30])) },
    ];
};

interface ReadonlyDepositCardPropsType {
    state: RollingNetDepositLimitItemState;
    depositLimitTime: 'daily' | 'weekly' | 'monthly';
    isDarkMode?: boolean;
    calculationType: getRollingLimits.CalculationTypeSchemaType;
}

export const DepositLimitReadonlyCard = observer('DepositLimitReadonlyCard', (props: ReadonlyDepositCardPropsType) => {
    const { state, depositLimitTime, calculationType } = props;
    const common = useCommon();
    const configComponents = ConfigComponents.get(common);
    const languagesState = LanguagesState.get(common);
    const usersState = state.usersState;
    const effectiveLimitType = resolveAccountLimitWindowType(
        state.getLimits()?.limitType,
        toWindowLengthInDays(depositLimitTime),
        calculationType,
        usersState.depositLimitConfigData.valueReady ?? undefined
    );
    const periodForDataTest =
        effectiveLimitType === 'rolling' && depositLimitTime === 'daily'
            ? '1-day'
            : effectiveLimitType === 'rolling' && depositLimitTime === 'weekly'
              ? '7-day'
              : effectiveLimitType === 'rolling' && depositLimitTime === 'monthly'
                ? '30-day'
                : depositLimitTime;

    const activeValue = state.getLimits()?.activeLimitValue;
    const headlineAmount =
        activeValue === undefined
            ? languagesState.getTranslation('account.deposit-limit-readonly.not-set', 'Not set')
            : configComponents.precision.newFromOld(activeValue).format(usersState.currency);
    const useCalendarLabels = shouldUseCalendarPeriodLabels(effectiveLimitType);
    const showCalendarResetRow = effectiveLimitType === 'calendar';

    return (
        <div data-test={`readonly-deposit-limit-card-${periodForDataTest}`}>
            <DepositLimitsPanel isDarkMode={props.isDarkMode}>
                <ReadonlyCardHeaderRow data-test={`readonly-limit-${periodForDataTest}`}>
                    <ReadonlyCardPeriod>
                        {renderLabel(languagesState, depositLimitTime, useCalendarLabels)}
                    </ReadonlyCardPeriod>
                    <ReadonlyCardAmount data-test={`readonly-limit-amount-${periodForDataTest}`}>
                        {headlineAmount}
                    </ReadonlyCardAmount>
                </ReadonlyCardHeaderRow>

                <TPWrapper>
                    {showCalendarResetRow ? (
                        <CalendarLimitAvailableAndResetRow
                            isDarkMode={props.isDarkMode}
                            data-test={`limit-available-reset-${periodForDataTest}`}
                        >
                            <span data-test={`limit-resets-on-${periodForDataTest}`}>
                                <I18n
                                    langKey='account.net-deposit-limits.resets-on.label'
                                    defaultText='Resets on '
                                />
                                {state.getFormattedDate(depositLimitTime)}
                            </span>
                            <span data-test={`limit-amount-available-${periodForDataTest}`}>
                                <I18n
                                    langKey='account.net-deposit-limits.available-line'
                                    defaultText='{amount} Available'
                                    params={{ amount: state.amountAvailable }}
                                />
                            </span>
                        </CalendarLimitAvailableAndResetRow>
                    ) : (
                        <ReadonlyCardAvailableAmountRow
                            isDarkMode={props.isDarkMode}
                            data-test={`limit-amount-remaining-${periodForDataTest}`}
                        >
                            <I18n
                                langKey='account.net-deposit-limits.available-line'
                                defaultText='{amount} Available'
                                params={{ amount: state.amountAvailable }}
                            />
                        </ReadonlyCardAvailableAmountRow>
                    )}
                </TPWrapper>
                {renderRollingLimitPending(configComponents, usersState, state.getLimits(), periodForDataTest)}
            </DepositLimitsPanel>
        </div>
    );
});

interface PropsType {
    state: RollingNetDepositLimitItemState;
    depositLimitTime: 'daily' | 'weekly' | 'monthly';
    calculationType?: getRollingLimits.CalculationTypeSchemaType;
}

export const TopUpLimitsProcedureItem = observer('TopUpLimitsItem', (props: PropsType) => {
    const { state, depositLimitTime, calculationType } = props;
    const common = useCommon();
    const configComponents = ConfigComponents.get(common);
    const languagesState = LanguagesState.get(common);
    const usersState = state.usersState;
    const effectiveCalculationType = calculationType ?? state.getLimits()?.calculationType ?? 'net';
    const effectiveLimitType = resolveAccountLimitWindowType(
        state.getLimits()?.limitType,
        toWindowLengthInDays(depositLimitTime),
        effectiveCalculationType,
        usersState.depositLimitConfigData.valueReady ?? undefined
    );
    const periodForDataTest =
        effectiveLimitType === 'rolling' && depositLimitTime === 'daily'
            ? '1-day'
            : effectiveLimitType === 'rolling' && depositLimitTime === 'weekly'
              ? '7-day'
              : effectiveLimitType === 'rolling' && depositLimitTime === 'monthly'
                ? '30-day'
                : depositLimitTime;
    const useCalendarLabels = shouldUseCalendarPeriodLabels(effectiveLimitType);
    const showCalendarResetRow = effectiveLimitType === 'calendar';

    return (
        <>
            <TopUpLimitsInput
                type='text'
                maxLength={10}
                label={renderLabel(languagesState, depositLimitTime, useCalendarLabels)}
                currency={BasicDataModel.get(common).moneySymbol}
                placeholder={state.activeLimit}
                state={state.limitAmount}
                inputRef={state.setAmountInputRef}
                onChange={state.onChangeInput}
                colorTheme='light'
                onBlur={state.onBlur}
                dataTest={`${periodForDataTest}`}
            />
            {renderRollingLimitPending(configComponents, usersState, state.getLimits(), periodForDataTest)}
            {state.errorMaxLimitExceeded ? (
                <Messages
                    type='error'
                    message={
                        <I18n
                            langKey='account.rolling-net-deposit-limits.max-limit-input'
                            defaultText='Please set limit below {maxLimitValue}'
                            params={{
                                maxLimitValue: state.formattedDepositLimitCeiling,
                            }}
                        />
                    }
                    dataTest={`limit-error-message-${periodForDataTest}`}
                />
            ) : null}

            <TPWrapper>
                {showCalendarResetRow ? (
                    <CalendarLimitAvailableAndResetRow data-test={`limit-available-reset-${periodForDataTest}`}>
                        <span data-test={`limit-resets-on-${periodForDataTest}`}>
                            <I18n
                                langKey='account.net-deposit-limits.resets-on.label'
                                defaultText='Resets on '
                            />
                            {state.getFormattedDate(depositLimitTime)}
                        </span>
                        <span data-test={`limit-amount-available-${periodForDataTest}`}>
                            <I18n
                                langKey='account.net-deposit-limits.available-line'
                                defaultText='{amount} Available'
                                params={{ amount: state.amountAvailable }}
                            />
                        </span>
                    </CalendarLimitAvailableAndResetRow>
                ) : (
                    <ReadonlyCardAvailableAmountRow data-test={`limit-amount-remaining-${periodForDataTest}`}>
                        <I18n
                            langKey='account.net-deposit-limits.available-line'
                            defaultText='{amount} Available'
                            params={{ amount: state.amountAvailable }}
                        />
                    </ReadonlyCardAvailableAmountRow>
                )}
            </TPWrapper>
        </>
    );
});
